import { useState, useEffect, useRef, useMemo, useLayoutEffect } from "react"
import Grid from "@mui/material/Grid"
import { HotelContractModel, SeasonModel, MealPlanTypeModel } from "../../models"
import { getMissingDateRanges, MissingDateRange } from "../../util/helpers"
import { DayWisePricingGroupLocal, SeasonDateRangeLocal, HotelContractProps, PeakDateRangeLocal } from "./hotelContractTypes"
import { dayWisePricingApiToLocal, localDayWiseGroupsToApi } from "./dayWisePricingMaps"
import { useHotelDayWisePricingContext } from "../../context/HotelDayWisePricingContext"
import ContractDates from "./contractDates"
import SeasonList from "./seasonList"
import DayWisePricing from "./dayWisePricing"
import GuestAgePolicy from "./guestAgePolicy"
import ExtraBedPricing from "./extraBedPricing"
import SeasonDateRanges from "./seasonDateRanges"
import RateStrucher from "./rateStrucher"
import "./hotelContract.scss"
import SurchargeModels from "./surchargeModels"
import PeakDateRange from "./peakDateRange"
import { useHotelGeneric } from "../../context/HotelGenericContext"
import {
    canAddSeason,
    collectHotelContractErrors,
    getFirstErrorMessage,
    getSeasonDependencyErrors,
    isContractWithoutSeason,
} from "./hotelContractValidationUtil"

// Helper function to convert timestamp or date string to YYYY-MM-DD format
const convertToDateString = (date: string | number | null | undefined): string => {
    if (!date) return ""
    if (typeof date === 'number') {
        return new Date(date).toISOString().split('T')[0]
    }
    return date
}
const HotelContract = ({
    contract,
    dayWisePricingScopeId,
    onChange,
    validateContractDates,
    onErrorStatusChange,
    showValidationErrors = false,
}: HotelContractProps) => {
    const { hotelGenericData } = useHotelGeneric()
    const { slices, patchSlice } = useHotelDayWisePricingContext()
    const isInitialMount = useRef(true)
    const lastSentData = useRef<string>("")
    const lastPushedDayWiseJsonRef = useRef<string>("")
    const lastErrorsJson = useRef<string | null>(null)
    const onErrorStatusChangeRef = useRef<typeof onErrorStatusChange>(onErrorStatusChange)
    const [contractStartDate, setContractStartDate] = useState<string>(convertToDateString(contract[0]?.contract_start_date))
    const [contractEndDate, setContractEndDate] = useState<string>(convertToDateString(contract[0]?.contract_end_date))
    const [contractDateErrors, setContractDateErrors] = useState<string[]>([])
    const [seasons, setSeasons] = useState<SeasonModel[]>(contract[0]?.season || [])
    console.log("Printing value of slices and dayWisePricingScopeId", slices, dayWisePricingScopeId)
    const dayWiseSlice = slices[dayWisePricingScopeId]
    const useDayWisePricing = dayWiseSlice?.flag_day_wise_pricing ?? !!contract[0]?.flag_day_wise_pricing
    const dayWiseGroups =
        dayWiseSlice?.dayWiseGroups ?? dayWisePricingApiToLocal(contract[0]?.day_wise_pricing)

    const contractIdForDayWiseLayout = contract[0]?.contract_id ?? ""
    useLayoutEffect(() => {
        if (!dayWisePricingScopeId) return
        patchSlice(dayWisePricingScopeId, (prev) =>
            prev ?? {
                flag_day_wise_pricing: !!contract[0]?.flag_day_wise_pricing,
                dayWiseGroups: dayWisePricingApiToLocal(contract[0]?.day_wise_pricing),
            },
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-seed when contract identity changes; day-wise payload syncs via onChange
    }, [dayWisePricingScopeId, contractIdForDayWiseLayout, patchSlice])

    const [freeChildAgeLimit, setFreeChildAgeLimit] = useState<number>(contract[0]?.free_child_age_limit || 4)
    const [chargeableChildAgeLimit, setChargeableChildAgeLimit] = useState<number>(contract[0]?.chargeable_child_age_limit || 11)
    const [seasonalExtraBedPricing, setSeasonalExtraBedPricing] = useState<boolean>(contract[0]?.flag_seasonal_extra_bed_pricing || false)

    // Surcharge model flags
    const [enablePeakDateSurcharges, setEnablePeakDateSurcharges] = useState<boolean>(contract[0]?.flag_enable_peak_date_surcharges || false)
    const [peakSurchargesVaryByRoomType, setPeakSurchargesVaryByRoomType] = useState<boolean>(contract[0]?.flag_peak_surcharge_vary_by_room_type || false)
    const [enablePerPersonSeasonalSurcharges, setEnablePerPersonSeasonalSurcharges] = useState<boolean>(contract[0]?.flag_enable_per_person_seasonal_surcharge || false)

    // Initialize peak date ranges from contract data
    const [peakDateRanges, setPeakDateRanges] = useState<PeakDateRangeLocal[]>(() => {
        const ranges: PeakDateRangeLocal[] = []
        contract[0]?.peak_date_surcharge_group?.forEach((group) => {
            group.peak_date_range?.forEach((range) => {
                ranges.push({
                    range_id: range.peak_date_range_id || `peak_${Date.now()}_${Math.random()}`,
                    start_date: convertToDateString(range.start_date),
                    end_date: convertToDateString(range.end_date),
                    flag_compulsory_meal: false, // This might need to come from another source
                    flag_different_surcharge: group.flag_different_surcharge || false,
                })
            })
        })
        // Return at least one default range if none exist
        return ranges.length > 0 ? ranges : [{
            range_id: `peak_${Date.now()}_1`,
            start_date: "",
            end_date: "",
            flag_compulsory_meal: false,
            flag_different_surcharge: false,
        }]
    })

    // Initialize seasonDateRanges from contract data (flatten all season_date_range arrays)
    const [seasonDateRanges, setSeasonDateRanges] = useState<SeasonDateRangeLocal[]>(() => {
        const ranges: SeasonDateRangeLocal[] = []
        contract[0]?.season?.forEach((season) => {
            season.season_date_range?.forEach((range) => {
                ranges.push({
                    range_id: range.season_date_range_id || `range_${Date.now()}_${Math.random()}`,
                    season_id: season.season_id || "",
                    start_date: convertToDateString(range.season_start_date),
                    end_date: convertToDateString(range.season_end_date),
                })
            })
        })
        return ranges
    })

    // Initialize rate structure type from contract data or default to first available
    const [rateStructureType, setRateStructureType] = useState<number | null>(() => {
        const contractRateStructureId = contract[0]?.rate_structure_type_id
        if (contractRateStructureId) {
            return contractRateStructureId
        }
        // Default to first rate structure type if available
        return 1
    })

    // Initialize selected meal plans from contract data
    const [selectedMealPlans, setSelectedMealPlans] = useState<number[]>(() => {
        if (contract[0]?.meal_plan_type && contract[0].meal_plan_type.length > 0) {
            return contract[0].meal_plan_type.map(mp => mp.meal_plan_type_id)
        }
        return []
    })

    // Initialize GIT/FIT values from contract data
    const [gitFitMinRoomCount, setGitFitMinRoomCount] = useState<number>(() => {
        return contract[0]?.git_fit_applied_on_minimum_room ?? 10
    })

    const [gitFitMinPersonCount, setGitFitMinPersonCount] = useState<number>(() => {
        return contract[0]?.git_fit_applied_on_minimum_pax ?? 15
    })

    // Helper function to get rate structure type name from ID
    const getRateStructureTypeName = (id: number | null): string => {
        if (!id) return ''
        const rateStructureItem = hotelGenericData.rate_structure_type.find(
            (item) => item.rate_structure_type_id === id
        )
        return rateStructureItem?.rate_structure_type_name || ''
    }

    // Handle meal plan selection changes
    const handleMealPlanChange = (mealPlanIds: number[]) => {
        setSelectedMealPlans(mealPlanIds)
    }

    // Notify parent whenever any state changes
    useEffect(() => {
        // Skip on initial mount to prevent infinite loop
        if (isInitialMount.current) {
            isInitialMount.current = false
            return
        }

        if (!onChange) return

        // Merge seasonDateRanges back into seasons. Always send season_date_range for every season
        // (including when flag_remaining_season is true — send that season's ranges, or [] if none).
        const seasonsWithDateRanges = seasons.map((season) => {
            const rangesForThisSeason = seasonDateRanges.filter((range) => range.season_id === season.season_id)
            return {
                ...season,
                season_date_range: rangesForThisSeason.map((range) => ({
                    season_date_range_id: (range.range_id.startsWith("range_") || range.range_id.startsWith("remaining_")) ? null : range.range_id,
                    season_start_date: range.start_date,
                    season_end_date: range.end_date,
                })),
            }
        })

        const convertedDayWisePricing = useDayWisePricing ? localDayWiseGroupsToApi(dayWiseGroups) : []
        lastPushedDayWiseJsonRef.current = JSON.stringify(convertedDayWisePricing)

        // Convert peakDateRanges to proper peak_date_surcharge_group structure
        // Split ranges into two groups based on flag_different_surcharge
        const convertedPeakDateSurchargeGroup = enablePeakDateSurcharges && peakDateRanges.length > 0 ? (() => {
            const groups = []

            // Group 1: Ranges with flag_different_surcharge = false (standard surcharge)
            const standardRanges = peakDateRanges.filter(r => !r.flag_different_surcharge)
            if (standardRanges.length > 0) {
                // Find existing group with flag_different_surcharge: false
                const existingStandardGroup = contract[0]?.peak_date_surcharge_group?.find(g => !g.flag_different_surcharge)
                groups.push({
                    peak_date_surcharge_group_id: existingStandardGroup?.peak_date_surcharge_group_id || null,
                    flag_different_surcharge: false,
                    standard_peak_season_surcharge: existingStandardGroup?.standard_peak_season_surcharge || null,
                    different_peak_season_surcharge: existingStandardGroup?.different_peak_season_surcharge || null,
                    peak_date_range: standardRanges.map((range) => ({
                        peak_date_range_id: range.range_id.startsWith("peak_") ? null : range.range_id,
                        start_date: range.start_date,
                        end_date: range.end_date,
                    })),
                    peak_date_surcharge_room_type_group: existingStandardGroup?.peak_date_surcharge_room_type_group || undefined,
                })
            }

            // Group 2: Ranges with flag_different_surcharge = true (different surcharge)
            const differentRanges = peakDateRanges.filter(r => r.flag_different_surcharge)
            if (differentRanges.length > 0) {
                // Find existing group with flag_different_surcharge: true
                const existingDifferentGroup = contract[0]?.peak_date_surcharge_group?.find(g => g.flag_different_surcharge)
                groups.push({
                    peak_date_surcharge_group_id: existingDifferentGroup?.peak_date_surcharge_group_id || null,
                    flag_different_surcharge: true,
                    standard_peak_season_surcharge: existingDifferentGroup?.standard_peak_season_surcharge || null,
                    different_peak_season_surcharge: existingDifferentGroup?.different_peak_season_surcharge || null,
                    peak_date_range: differentRanges.map((range) => ({
                        peak_date_range_id: range.range_id.startsWith("peak_") ? null : range.range_id,
                        start_date: range.start_date,
                        end_date: range.end_date,
                    })),
                    peak_date_surcharge_room_type_group: existingDifferentGroup?.peak_date_surcharge_room_type_group || undefined,
                })
            }

            return groups
        })() : []

        // Convert selected meal plans to MealPlanTypeModel format
        const convertedMealPlans: MealPlanTypeModel[] = getRateStructureTypeName(rateStructureType) === "Meal Plan"
            ? selectedMealPlans.map((mealPlanId) => {
                // Find existing meal plan data from contract to preserve IDs and pricing
                const existingMealPlan = contract[0]?.meal_plan_type?.find(
                    mp => mp.meal_plan_type_id === mealPlanId
                )

                // Get meal plan name from generic data
                const genericMealPlan = hotelGenericData.meal_plan_type.find(
                    mp => mp.meal_plan_type_id === mealPlanId
                )

                return {
                    contract_meal_plan_id: existingMealPlan?.contract_meal_plan_id || null,
                    meal_plan_type_id: mealPlanId,
                    meal_plan_type_name: genericMealPlan?.meal_plan_type_name || "",
                    pax_surcharge: existingMealPlan?.pax_surcharge || null,
                    child_with_bed_surcharge: existingMealPlan?.child_with_bed_surcharge || null,
                    child_without_bed_surcharge: existingMealPlan?.child_without_bed_surcharge || null,
                    adult_extra_bed_surcharge: existingMealPlan?.adult_extra_bed_surcharge || null,
                }
            })
            : []

        // Convert local state back to HotelContractModel format
        const updatedContract: HotelContractModel = {
            contract_id: contract[0]?.contract_id || null,
            contract_start_date: contractStartDate || null,
            contract_end_date: contractEndDate || null,
            hotel_id: contract[0]?.hotel_id || "",
            flag_day_wise_pricing: useDayWisePricing,
            flag_seasonal_extra_bed_pricing: seasonalExtraBedPricing,
            flag_enable_peak_date_surcharges: enablePeakDateSurcharges,
            flag_peak_surcharge_vary_by_room_type: peakSurchargesVaryByRoomType,
            flag_enable_per_person_seasonal_surcharge: enablePerPersonSeasonalSurcharges,
            free_child_age_limit: freeChildAgeLimit,
            chargeable_child_age_limit: chargeableChildAgeLimit,
            extra_bed_price: contract[0]?.extra_bed_price || null,
            child_without_bed_price: contract[0]?.child_without_bed_price ?? null,
            child_with_bed: contract[0]?.child_with_bed || null,
            adult_with_bed: contract[0]?.adult_with_bed || null,
            flag_adult_with_bed_is_percentage: contract[0]?.flag_adult_with_bed_is_percentage,
            flag_child_with_bed_is_percentage: contract[0]?.flag_child_with_bed_is_percentage,
            flag_child_without_bed_is_percentage: contract[0]?.flag_child_without_bed_is_percentage,
            surcharge_per_additional_pax: contract[0]?.surcharge_per_additional_pax || null,
            rate_structure_type_id: rateStructureType,
            season: seasonsWithDateRanges,
            seasonal_extra_bed_pricing: contract[0]?.seasonal_extra_bed_pricing || [],
            day_wise_pricing: useDayWisePricing ? convertedDayWisePricing : [],
            hotel_room_pricing: contract[0]?.hotel_room_pricing || [],
            // Preserve rate-table day group pricing returned from backend
            // (otherwise it gets dropped when contract is edited and Rates tab can't render values)
            hotel_room_day_group_pricing: contract[0]?.hotel_room_day_group_pricing || [],
            // Preserve meal plan rows returned from backend (Rates tab uses this)
            meal_plan: contract[0]?.meal_plan || [],
            // Preserve room-specific different surcharges (Rates tab uses this)
            room_specific_different_surcharge: contract[0]?.room_specific_different_surcharge || [],
            // Preserve per-person seasonal surcharges (Rates tab uses this)
            per_person_seasonal_surcharge: contract[0]?.per_person_seasonal_surcharge || [],
            // Preserve the shared peak-date surcharge VND/% unit (edited on the Rates tab, not here).
            // Dropping it reshaped the contract on mount → false "unsaved changes" + lost on save.
            flag_peak_date_surcharge_is_percentage: contract[0]?.flag_peak_date_surcharge_is_percentage,
            meal_plan_type: convertedMealPlans,
            peak_date_surcharge_group:enablePeakDateSurcharges === true && convertedPeakDateSurchargeGroup.length > 0 ? convertedPeakDateSurchargeGroup : undefined,
            git_fit_applied_on_minimum_room: rateStructureType === 3 ? gitFitMinRoomCount : null,
            git_fit_applied_on_minimum_pax: rateStructureType === 3 ? gitFitMinPersonCount : null,
        }

        // Only call onChange if data has actually changed
        const currentDataString = JSON.stringify(updatedContract)
        if (currentDataString !== lastSentData.current) {
            lastSentData.current = currentDataString
            onChange(updatedContract)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contractStartDate, contractEndDate, seasons, useDayWisePricing, dayWiseGroups, freeChildAgeLimit, chargeableChildAgeLimit, seasonalExtraBedPricing, seasonDateRanges, enablePeakDateSurcharges, peakSurchargesVaryByRoomType, enablePerPersonSeasonalSurcharges, peakDateRanges, rateStructureType, selectedMealPlans, gitFitMinRoomCount, gitFitMinPersonCount, contract, onChange, hotelGenericData.meal_plan_type])

    // Memoize the filtered non-remaining date ranges for dependency tracking
    const nonRemainingDateRangesKey = useMemo(() => {
        return JSON.stringify(
            seasonDateRanges
                .filter((range) => !range.range_id.startsWith("remaining_") && !seasons.find((s) => s.season_id === range.season_id && s.flag_remaining_season))
                .map((r) => ({ id: r.season_id, start: r.start_date, end: r.end_date }))
        )
    }, [seasonDateRanges, seasons])

    // // Auto-calculate remaining dates for season with flag_remaining_season = true
    useEffect(() => {
        // Find season marked as "remaining dates"
        const remainingSeason = seasons.find((s) => s.flag_remaining_season)

        if (!remainingSeason || !contractStartDate || !contractEndDate) {
            // If no remaining season, ensure no ranges are marked as remaining
            setSeasonDateRanges((prev) => prev.filter((range) => !range.range_id.startsWith("remaining_")))
            return
        }

        // Get date ranges for OTHER seasons (not the remaining season)
        const otherSeasonsRanges = seasonDateRanges.filter((range) => range.season_id !== remainingSeason.season_id && !range.range_id.startsWith("remaining_"))

        // Build contract model with current state for calculation
        const currentContractData: HotelContractModel = {
            contract_id: contract[0]?.contract_id || null,
            contract_start_date: contractStartDate,
            contract_end_date: contractEndDate,
            hotel_id: contract[0]?.hotel_id || "",
            flag_day_wise_pricing: useDayWisePricing,
            flag_seasonal_extra_bed_pricing: seasonalExtraBedPricing,
            flag_enable_peak_date_surcharges: enablePeakDateSurcharges,
            flag_peak_surcharge_vary_by_room_type: peakSurchargesVaryByRoomType,
            flag_enable_per_person_seasonal_surcharge: enablePerPersonSeasonalSurcharges,
            free_child_age_limit: freeChildAgeLimit,
            chargeable_child_age_limit: chargeableChildAgeLimit,
            extra_bed_price: contract[0]?.extra_bed_price || null,
            child_without_bed_price: contract[0]?.child_without_bed_price ?? null,
            child_with_bed: contract[0]?.child_with_bed || null,
            adult_with_bed: contract[0]?.adult_with_bed || null,
            flag_adult_with_bed_is_percentage: contract[0]?.flag_adult_with_bed_is_percentage,
            flag_child_with_bed_is_percentage: contract[0]?.flag_child_with_bed_is_percentage,
            flag_child_without_bed_is_percentage: contract[0]?.flag_child_without_bed_is_percentage,
            surcharge_per_additional_pax: contract[0]?.surcharge_per_additional_pax || null,
            rate_structure_type_id: contract[0]?.rate_structure_type_id || null,
            season: seasons.map((season) => ({
                ...season,
                season_name: season.season_name?.trim() ?? null,
                season_date_range: seasonDateRanges
                    .filter((range) => range.season_id === season.season_id && !range.range_id.startsWith("remaining_"))
                    .map((range) => ({
                        season_date_range_id: range.range_id.startsWith("range_") ? null : range.range_id,
                        season_start_date: range.start_date,
                        season_end_date: range.end_date,
                    })),
            })),
            seasonal_extra_bed_pricing: contract[0]?.seasonal_extra_bed_pricing || [],
            day_wise_pricing: contract[0]?.day_wise_pricing || [],
            hotel_room_pricing: contract[0]?.hotel_room_pricing || [],
            per_person_seasonal_surcharge: contract[0]?.per_person_seasonal_surcharge || [],
            room_specific_different_surcharge: contract[0]?.room_specific_different_surcharge || [],
            git_fit_applied_on_minimum_room: contract[0]?.git_fit_applied_on_minimum_room || null,
            git_fit_applied_on_minimum_pax: contract[0]?.git_fit_applied_on_minimum_pax || null,
        }

        // Calculate missing date ranges
        const missingRanges: MissingDateRange[] = getMissingDateRanges(currentContractData)

        // Get existing remaining ranges to preserve their IDs
        const existingRemainingRanges = seasonDateRanges.filter((range) => range.range_id.startsWith("remaining_") && range.season_id === remainingSeason.season_id)

        // Add calculated missing ranges - reuse existing IDs if available, otherwise create new ones
        const newRanges: SeasonDateRangeLocal[] = missingRanges.map((range, index) => {
            // Check if we have an existing range with the same dates
            const existingRange = existingRemainingRanges.find(
                (er) => er.start_date === range.start && er.end_date === range.end
            )

            return {
                range_id: existingRange ? existingRange.range_id : `remaining_${Date.now()}_${index}`,
                season_id: remainingSeason.season_id || "",
                start_date: range.start,
                end_date: range.end,
            }
        })

        // Only update if the ranges actually changed
        const rangesChanged = JSON.stringify(existingRemainingRanges.map(r => ({ start: r.start_date, end: r.end_date }))) !==
            JSON.stringify(newRanges.map(r => ({ start: r.start_date, end: r.end_date })))

        if (rangesChanged || existingRemainingRanges.length === 0) {
            // Update state: keep other seasons' ranges + add new calculated remaining ranges
            setSeasonDateRanges([...otherSeasonsRanges, ...newRanges])
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        seasons,
        contractStartDate,
        contractEndDate,
        chargeableChildAgeLimit,
        contract,
        freeChildAgeLimit,
        seasonalExtraBedPricing,
        useDayWisePricing,
        nonRemainingDateRangesKey,
        // Note: seasonDateRanges is NOT in dependencies to avoid infinite loop
        // nonRemainingDateRangesKey tracks the relevant changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ])

    // useEffect(() => {
    //     if (seasons.length === 0) {
    //         handleAddSeason()
    //     }
    // }, [seasons])

    useEffect(() => {
        onErrorStatusChangeRef.current = onErrorStatusChange
    }, [onErrorStatusChange])

    const handleContractStartDateChange = (date: string) => {
        setContractStartDate(date)
        // Validate contract dates
        if (validateContractDates) {
            try {
                const errors = validateContractDates(date, contractEndDate)
                setContractDateErrors(Array.isArray(errors) ? errors : [])
            } catch (error) {
                console.error("Error validating contract dates:", error)
                setContractDateErrors([])
            }
        }
    }

    const handleContractEndDateChange = (date: string) => {
        setContractEndDate(date)
        // Validate contract dates
        if (validateContractDates) {
            try {
                const errors = validateContractDates(contractStartDate, date)
                setContractDateErrors(Array.isArray(errors) ? errors : [])
            } catch (error) {
                console.error("Error validating contract dates:", error)
                setContractDateErrors([])
            }
        }
    }

    const handleAddSeason = () => {
        // Seasons are optional, but a new row is only allowed once every existing row has a name
        if (seasons.length > 0 && !canAddSeason(seasons)) return
        const newSeason: SeasonModel = {
            season_id: `season_${Date.now()}`,
            season_name: "",
            flag_remaining_season: false,
            flag_compulsory_meal: false,
            season_date_range: [],
        }
        setSeasons([...seasons, newSeason])
    }

    useEffect(() => {
        if (seasons.length === 0) {
            handleAddSeason()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seasons])

    const handleDeleteSeason = (index: number) => {
        const updatedSeasons = seasons.filter((_, i) => i !== index)
        setSeasons(updatedSeasons)
    }

    const handleSeasonNameChange = (index: number, value: string) => {
        const updatedSeasons = [...seasons]
        updatedSeasons[index] = { ...updatedSeasons[index], season_name: value }
        setSeasons(updatedSeasons)
    }

    // Helper function to check if a season name is duplicate within the contract
    const getSeasonNameError = (index: number, seasonName: string): string | undefined => {
        if (!showValidationErrors) return undefined

        // A single empty season row means "no season" — only an error when that row already has date data
        if (isContractWithoutSeason(seasons)) {
            return getFirstErrorMessage(seasonDependencyErrors, "seasonName")
        }

        // Check if empty
        if (!seasonName || seasonName.trim() === "") {
            return "Season name is required"
        }

        // Check for duplicates within this contract
        const duplicateIndex = seasons.findIndex((s, i) =>
            i !== index &&

            s.season_name &&
            s.season_name.trim().toLowerCase() === seasonName.trim().toLowerCase()
        )

        if (duplicateIndex !== -1) {
            return "Season name must be unique within this contract"
        }

        return undefined
    }

    const setUseDayWisePricing = (value: boolean) => {
        patchSlice(dayWisePricingScopeId, (prev) => ({
            flag_day_wise_pricing: value,
            dayWiseGroups: value === false ? [] : (
                prev?.flag_day_wise_pricing === false ? dayWisePricingApiToLocal(contract[0]?.day_wise_pricing) : (prev?.dayWiseGroups ?? dayWisePricingApiToLocal(contract[0]?.day_wise_pricing))
            ),
        }))
    }

    const handleAddGroup = () => {
        patchSlice(dayWisePricingScopeId, (prev) => {
            const groups = prev!.dayWiseGroups
            const nextSequence =
                groups.reduce((max, g) => {
                    const seq = typeof g.sequence === "number" && Number.isFinite(g.sequence) ? g.sequence : 0
                    return Math.max(max, seq)
                }, 0) + 1
            const newGroup: DayWisePricingGroupLocal = {
                day_wise_pricing_group_id: `group_${Date.now()}`,
                day_wise_pricing_group_name: "",
                days: [],
                flag_surchange_applied: false,
                surcharge_amount: null,
                flag_surcharge_amount_is_percentage: false,
                day_wise_pricing_group_surcharge: [],
                flag_disabled: false,
                flag_enable_day_group_in_rate_table: false,
                sequence: nextSequence,
            }
            return { ...prev!, dayWiseGroups: [...groups, newGroup] }
        })
    }

    const handleDeleteGroup = (index: number) => {
        patchSlice(dayWisePricingScopeId, (prev) => {
            const groups = prev!.dayWiseGroups
            if (prev!.flag_day_wise_pricing && groups.length <= 1) return prev!
            return { ...prev!, dayWiseGroups: groups.filter((_, i) => i !== index) }
        })
    }

    const handleGroupNameChange = (index: number, value: string) => {
        patchSlice(dayWisePricingScopeId, (prev) => {
            const groups = [...prev!.dayWiseGroups]
            groups[index] = { ...groups[index], day_wise_pricing_group_name: value }
            return { ...prev!, dayWiseGroups: groups }
        })
    }

    const handleDayToggle = (groupIndex: number, dayId: number) => {
        patchSlice(dayWisePricingScopeId, (prev) => {
            const updatedGroups = [...prev!.dayWiseGroups]
            const group = updatedGroups[groupIndex]
            if (group.days.includes(dayId)) {
                group.days = group.days.filter((d) => d !== dayId)
            } else {
                group.days = [...group.days, dayId]
            }
            return { ...prev!, dayWiseGroups: updatedGroups }
        })
    }

    const handleSurchargeToggle = (groupIndex: number) => {
        patchSlice(dayWisePricingScopeId, (prev) => {
            const updatedGroups = [...prev!.dayWiseGroups]
            updatedGroups[groupIndex].flag_surchange_applied = !updatedGroups[groupIndex].flag_surchange_applied

            const anySurchargeApplied = updatedGroups.some((group) => group.flag_surchange_applied)
            if (anySurchargeApplied) {
                updatedGroups.forEach((group) => {
                    group.flag_enable_day_group_in_rate_table = false
                })
            }

            return { ...prev!, dayWiseGroups: updatedGroups }
        })
    }

    const handleEnableDayGroupInRateTableToggle = (value: boolean) => {
        patchSlice(dayWisePricingScopeId, (prev) => {
            const updatedGroups = prev!.dayWiseGroups.map((group) => ({
                ...group,
                flag_enable_day_group_in_rate_table: value,
                flag_surchange_applied: value === true ? false : group.flag_surchange_applied,
            }))
            return { ...prev!, dayWiseGroups: updatedGroups }
        })
    }

    const handleEnablePeakDateSurchargesChange = (value: boolean) => {
        setEnablePeakDateSurcharges(value)
        
        // If disabling peak date surcharges, clear all peak date ranges
        if (!value) {
            setPeakDateRanges([{
                range_id: `peak_${Date.now()}_1`,
                start_date: "",
                end_date: "",
                flag_compulsory_meal: false,
                flag_different_surcharge: false,
            }])
        }
    }

    const handleAddDateRange = (seasonId: string) => {
        const newRange: SeasonDateRangeLocal = {
            range_id: `range_${Date.now()}`,
            season_id: seasonId,
            start_date: "",
            end_date: "",
        }
        setSeasonDateRanges([...seasonDateRanges, newRange])
    }

    const handleCreateNewRange = (seasonId: string, startDate: string) => {
        const newRange: SeasonDateRangeLocal = {
            range_id: `range_${Date.now()}`,
            season_id: seasonId,
            start_date: startDate,
            end_date: "",
        }
        setSeasonDateRanges([...seasonDateRanges, newRange])
    }

    // const handleCompulsoryMealChange = (seasonIndex: number, checked: boolean) => {
    //     const updatedSeasons = seasons.map((season, idx) => ({
    //         ...season,
    //         flag_compulsory_meal: idx === seasonIndex ? checked : false,
    //     }))
    //     setSeasons(updatedSeasons)
    // }

    const handleDeleteDateRange = (rangeId: string) => {
        setSeasonDateRanges(seasonDateRanges.filter((range) => range.range_id !== rangeId))
    }

    const handleDateRangeChange = (rangeId: string, field: "start_date" | "end_date", value: string) => {
        const updatedRanges = seasonDateRanges.map((range) => (range.range_id === rangeId ? { ...range, [field]: value } : range))
        setSeasonDateRanges(updatedRanges)
    }

    // Helper function to check if dates overlap
    const datesOverlap = (start1: string, end1: string, start2: string, end2: string): boolean => {
        if (!start1 || !end1 || !start2 || !end2) return false
        const s1 = new Date(start1)
        const e1 = new Date(end1)
        const s2 = new Date(start2)
        const e2 = new Date(end2)
        return s1 <= e2 && s2 <= e1
    }

    // Validate date range against contract period
    const isDateWithinContract = (date: string): boolean => {
        if (!date || !contractStartDate || !contractEndDate) return true
        const d = new Date(date)
        const start = new Date(contractStartDate)
        const end = new Date(contractEndDate)
        return d >= start && d <= end
    }

    // Check for overlapping date ranges (both same season and different seasons)
    const getDateRangeErrors = (rangeId: string, startDate: string, endDate: string): string[] => {
        try {
            const errors: string[] = []

            // Find the season this range belongs to
            const currentRange = seasonDateRanges.find((r) => r.range_id === rangeId)
            if (!currentRange) return errors

            // Find the season object
            const currentSeason = seasons.find((s) => s.season_id === currentRange.season_id)

            // Skip validation for "Remaining Dates" season (auto-calculated ranges)
            if (currentSeason?.flag_remaining_season || rangeId.startsWith("remaining_")) {
                return errors // No validation for remaining dates
            }

            // Check if dates are within contract period
            if (startDate && !isDateWithinContract(startDate)) {
                errors.push("Start date must be within contract period")
            }
            if (endDate && !isDateWithinContract(endDate)) {
                errors.push("End date must be within contract period")
            }

            // Check if start date is before end date
            if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
                errors.push("Start date must be before end date")
            }

            // Check for overlaps with ALL other date ranges (same season AND different seasons)
            // BUT exclude ranges from "Remaining Dates" seasons
            if (startDate && endDate) {
                seasonDateRanges.forEach((range) => {
                    // Skip checking against itself
                    if (range.range_id !== rangeId && range.start_date && range.end_date) {
                        // Skip checking against "Remaining Dates" season ranges
                        const otherSeason = seasons.find((s) => s.season_id === range.season_id)
                        if (otherSeason?.flag_remaining_season || range.range_id.startsWith("remaining_")) {
                            return // Skip validation against remaining dates
                        }

                        if (datesOverlap(startDate, endDate, range.start_date, range.end_date)) {
                            const season = seasons.find((s) => s.season_id === range.season_id)

                            // Check if it's same season or different season
                            if (range.season_id === currentRange.season_id) {
                                errors.push("Date ranges within the same season cannot overlap")
                            } else {
                                errors.push(`Overlaps with ${season?.season_name || "another season"}`)
                            }
                        }
                    }
                })
            }

            return errors
        } catch (error) {
            console.error("Error validating date ranges:", error)
            return []
        }
    }

    // Check for overlapping peak date ranges
    const getPeakDateRangeErrors = (rangeId: string, startDate: string, endDate: string): string[] => {
        try {
            const errors: string[] = []

            // Find the current range
            const currentRange = peakDateRanges.find((r) => r.range_id === rangeId)
            if (!currentRange) return errors

            // Check if dates are within contract period
            if (startDate && !isDateWithinContract(startDate)) {
                errors.push("Start date must be within contract period")
            }
            if (endDate && !isDateWithinContract(endDate)) {
                errors.push("End date must be within contract period")
            }

            // Check if start date is before end date
            if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
                errors.push("Start date must be before end date")
            }

            // Check for overlaps with other peak date ranges
            if (startDate && endDate) {
                peakDateRanges.forEach((range) => {
                    // Skip checking against itself
                    if (range.range_id !== rangeId && range.start_date && range.end_date) {
                        if (datesOverlap(startDate, endDate, range.start_date, range.end_date)) {
                            errors.push("Peak date ranges cannot overlap")
                        }
                    }
                })
            }

            return errors
        } catch (error) {
            console.error("Error validating peak date ranges:", error)
            return []
        }
    }

    // Get rate structure error messages
    const getMealPlanError = (): string | undefined => {
        if (!showValidationErrors) return undefined
        if (rateStructureType === 2 && selectedMealPlans.length === 0) {
            return "At least one meal plan must be selected"
        }
        return undefined
    }

    const getGitFitError = (): string | undefined => {
        if (!showValidationErrors) return undefined
        if (rateStructureType === 3) {
            if (gitFitMinRoomCount <= 0 || !Number.isInteger(gitFitMinRoomCount)) {
                return "Rooms must be a positive whole number"
            }
            if (gitFitMinPersonCount <= 0 || !Number.isInteger(gitFitMinPersonCount)) {
                return "Persons must be a positive whole number"
            }
        }
        return undefined
    }

    // Handle remaining dates checkbox (single choice)
    const handleRemainingDatesChange = (seasonIndex: number, checked: boolean) => {
        const updatedSeasons = seasons.map((season, idx) => ({
            ...season,
            flag_remaining_season: idx === seasonIndex ? checked : false,
        }))
        setSeasons(updatedSeasons)
    }

    // Get background color for season row
    const getSeasonRowColor = (seasonIndex: number): string => {
        const colors = ["#f0f8ff", "#fff0f5", "#f0fff0", "#fffacd", "#ffe4e1", "#f0e68c"]
        return colors[seasonIndex % colors.length]
    }

    // Real-time validation: computed after the date-range helpers above are defined
    const isWithoutSeason = isContractWithoutSeason(seasons)
    const seasonDependencyErrors = getSeasonDependencyErrors({
        seasons,
        seasonDateRanges,
        useDayWisePricing,
        dayWiseGroups,
        enablePerPersonSeasonalSurcharges,
        seasonalExtraBedPricing,
    })
    const contractValidationErrors = collectHotelContractErrors({
        seasons,
        seasonDateRanges,
        useDayWisePricing,
        dayWiseGroups,
        enablePerPersonSeasonalSurcharges,
        seasonalExtraBedPricing,
        contractDateErrors,
        contractStartDate,
        contractEndDate,
        rateStructureType,
        selectedMealPlans,
        gitFitMinRoomCount,
        gitFitMinPersonCount,
        enablePeakDateSurcharges,
        peakDateRanges,
        getDateRangeErrors,
        getPeakDateRangeErrors,
    })
    const contractValidationErrorsJson = JSON.stringify(contractValidationErrors)

    // Notify parent only when the error list actually changes (prevents render loops)
    useEffect(() => {
        if (!onErrorStatusChangeRef.current) return
        if (lastErrorsJson.current === contractValidationErrorsJson) return
        lastErrorsJson.current = contractValidationErrorsJson
        onErrorStatusChangeRef.current(JSON.parse(contractValidationErrorsJson))
    }, [contractValidationErrorsJson])

    const getSeasonDependencyInlineError = (key: "perPersonSeasonalSurcharge" | "dayWisePricing" | "extraBedPricing"): string | undefined =>
        showValidationErrors ? getFirstErrorMessage(seasonDependencyErrors, key) : undefined

    return (
        <>

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 5 }} style={{ borderRight: "1px solid #e0e0e0" }}>

                    <ContractDates
                        contractStartDate={contractStartDate}
                        contractEndDate={contractEndDate}
                        onStartDateChange={handleContractStartDateChange}
                        onEndDateChange={handleContractEndDateChange}
                        contractDateErrors={contractDateErrors}
                        showValidationErrors={showValidationErrors}
                    />

                    <SeasonList
                        seasons={seasons}
                        onSeasonNameChange={handleSeasonNameChange}
                        onDeleteSeason={handleDeleteSeason}
                        onAddSeason={handleAddSeason}
                        canAddSeason={canAddSeason(seasons)}
                        getSeasonNameError={getSeasonNameError}
                    />

                    <div style={{ marginTop: "20px" }} >
                        <SurchargeModels
                            enablePeakDateSurcharges={enablePeakDateSurcharges}
                            peakSurchargesVaryByRoomType={peakSurchargesVaryByRoomType}
                            enablePerPersonSeasonalSurcharges={enablePerPersonSeasonalSurcharges}
                            onEnablePeakDateSurchargesChange={handleEnablePeakDateSurchargesChange}
                            onPeakSurchargesVaryByRoomTypeChange={setPeakSurchargesVaryByRoomType}
                            onEnablePerPersonSeasonalSurchargesChange={setEnablePerPersonSeasonalSurcharges}
                            perPersonSeasonalSurchargeError={getSeasonDependencyInlineError("perPersonSeasonalSurcharge")}
                        />
                    </div>

                    <DayWisePricing
                        useDayWisePricing={useDayWisePricing}
                        dayWiseGroups={dayWiseGroups}
                        onUseDayWisePricingChange={setUseDayWisePricing}
                        onGroupNameChange={handleGroupNameChange}
                        onDayToggle={handleDayToggle}
                        onSurchargeToggle={handleSurchargeToggle}
                        onDeleteGroup={handleDeleteGroup}
                        onAddGroup={handleAddGroup}
                        onEnableDayGroupInRateTableToggle={handleEnableDayGroupInRateTableToggle}
                        showValidationErrors={showValidationErrors}
                        seasonDependencyError={getSeasonDependencyInlineError("dayWisePricing")}
                    />

                    <GuestAgePolicy
                        freeChildAgeLimit={freeChildAgeLimit}
                        chargeableChildAgeLimit={chargeableChildAgeLimit}
                        onFreeChildAgeLimitChange={setFreeChildAgeLimit}
                        onChargeableChildAgeLimitChange={setChargeableChildAgeLimit}
                    />

                    <ExtraBedPricing
                        seasonalExtraBedPricing={seasonalExtraBedPricing}
                        onSeasonalExtraBedPricingChange={setSeasonalExtraBedPricing}
                        error={getSeasonDependencyInlineError("extraBedPricing")}
                    />

                </Grid>
                <Grid size={{ xs: 12, sm: 12, md: 12, lg: 7 }}>
                    <RateStrucher
                        value={rateStructureType}
                        onChange={(value) => setRateStructureType(value)}
                        rateStructureTypes={hotelGenericData.rate_structure_type}
                        mealPlanTypes={hotelGenericData.meal_plan_type}
                        selectedMealPlans={selectedMealPlans}
                        onMealPlanChange={handleMealPlanChange}
                        gitFitMinRoomCount={gitFitMinRoomCount}
                        gitFitMinPersonCount={gitFitMinPersonCount}
                        onGitFitChange={(rooms, persons) => {
                            setGitFitMinRoomCount(rooms)
                            setGitFitMinPersonCount(persons)
                        }}
                        showValidationErrors={showValidationErrors}
                        mealPlanError={getMealPlanError()}
                        gitFitError={getGitFitError()}
                    />
                    <SeasonDateRanges
                        seasons={seasons}
                        seasonDateRanges={seasonDateRanges}
                        contractStartDate={contractStartDate}
                        contractEndDate={contractEndDate}
                        onDateRangeChange={handleDateRangeChange}
                        onAddDateRange={handleAddDateRange}
                        onDeleteDateRange={handleDeleteDateRange}
                        onRemainingDatesChange={handleRemainingDatesChange}
                        // onCompulsoryMealChange={handleCompulsoryMealChange}
                        onCreateNewRange={handleCreateNewRange}
                        getDateRangeErrors={getDateRangeErrors}
                        getSeasonRowColor={getSeasonRowColor}
                        // Date ranges are not required when the contract has no season
                        showValidationErrors={showValidationErrors && !isWithoutSeason}
                    />
                    {enablePeakDateSurcharges && (
                        <div style={{ marginTop: "20px" }} >
                            <PeakDateRange
                                contractStartDate={contractStartDate}
                                contractEndDate={contractEndDate}
                                showValidationErrors={showValidationErrors}
                                peakDateRanges={peakDateRanges}
                                onPeakDateRangesChange={setPeakDateRanges}
                                getPeakDateRangeErrors={getPeakDateRangeErrors}
                            />
                        </div>
                    )}
                </Grid>
            </Grid>
        </>
    )
}

export default HotelContract
